import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { colors } from '@classy/design-tokens';
import styles from './FireworkBurst.module.css';

export interface FireworkBurstProps {
  x: number;
  y: number;
}

const PARTICLE_COUNT = 28;
// Confetti palette mixes the accent token with primary/secondary colors
// pulled from the design-tokens scope (no inline hex literals).
const COLORS = [colors.accent, colors.cardBg, colors.textPrimary];
const DURATION_MS = 1200;

interface Particle {
  id: number;
  angle: number;
  color: string;
}

let nextId = 0;

export function FireworkBurst({ x, y }: FireworkBurstProps) {
  const [done, setDone] = useState(false);
  const particlesRef = useRef<Particle[] | null>(null);

  if (particlesRef.current === null) {
    const arr: Particle[] = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      arr.push({
        id: nextId++,
        angle: (i / PARTICLE_COUNT) * Math.PI * 2,
        color: COLORS[i % COLORS.length]!,
      });
    }
    particlesRef.current = arr;
  }

  useEffect(() => {
    const handle = setTimeout(() => setDone(true), DURATION_MS);
    return () => clearTimeout(handle);
  }, []);

  if (done) return null;

  return (
    <div
      className={styles.burst}
      style={{ left: `${x}px`, top: `${y}px` }}
      aria-hidden="true"
      data-testid="firework-burst"
    >
      {particlesRef.current.map((p) => {
        const dx = Math.cos(p.angle) * 120;
        const dy = Math.sin(p.angle) * 120;
        return (
          <span
            key={p.id}
            className={styles.particle}
            style={
              {
                background: p.color,
                ['--dx' as string]: `${dx}px`,
                ['--dy' as string]: `${dy}px`,
              } as CSSProperties
            }
          />
        );
      })}
    </div>
  );
}
